import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React from 'react';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Roles, ServiceCategory } from '@prisma/client';
import { capitalize } from '@mui/material';
import { addMonths } from 'date-fns';
import {
  fetchReportAdditionalServicesPage,
  fetchReportAppointmentsPage,
  fetchReportFilters,
  fetchReportOrdersPage,
  fetchReportTotals,
} from '../../services/reports';
import useUser from '../global/useUser';
import { FilterOption, LoadedUser } from '../../interfaces';
import {
  generateColorForArtist,
  getArtistOptions,
} from '../../services/business';
import { getArtistName } from '../../utils/global/helper';
import { DateType, TransactionType } from '../../types/reports';
import { getBusinessTripName } from '../../helpers/businesses';
import { getInitialFilterValue } from '../../helpers/global';
import useReportsTables, {
  REPORTS_TABLE_NAMES,
  ReportsTableName,
} from './useReportsTables';

function useReports() {
  const { user } = useUser<LoadedUser>();

  const availableEmployees = React.useMemo<FilterOption[]>(() => {
    if (user.role === Roles.SHOP_MANAGER && user.canViewReports) {
      return getArtistOptions(user);
    }
    return [
      {
        label: getArtistName(user.employee),
        value: user.userId,
        color: generateColorForArtist(user.userId, user),
      },
    ];
  }, [user]);

  const tripsOptions = React.useMemo(() => {
    if (!user.business.businessTrips?.length) return [];

    const tempTripOptions = [{ label: 'Home Studio', value: '' }];
    user.business.businessTrips.forEach(trip => {
      tempTripOptions.push({
        label: getBusinessTripName(trip),
        value: trip.tripId,
      });
    });
    return tempTripOptions;
  }, [user]);

  const availableServices = React.useMemo(() => {
    const initialServices: FilterOption[] = [
      {
        label: 'All services',
        value: 'ALL',
      },
      {
        label: 'Tattoos',
        value: ServiceCategory.TATTOO,
      },
    ];

    if (user.business.services?.length) {
      const categories = new Set<ServiceCategory>();
      user.business.services.forEach(s => {
        categories.add(s.serviceCategory as ServiceCategory);
      });
      initialServices.push(
        ...Array.from(categories).map(category => ({
          label: capitalize(category.toLowerCase().replace('_', ' ')),
          value: category,
        })),
      );
    }

    return initialServices;
  }, [user.business.services]);

  const dateTypeOptions: FilterOption[] = React.useMemo(
    () => [
      {
        label: 'Filter by payment date',
        value: 'payment',
      },
      {
        label: 'Filter by appointment date',
        value: 'appointment',
      },
    ],
    [],
  );

  const ordersOptions: FilterOption[] = React.useMemo(
    () => [
      {
        label: 'All transactions',
        value: 'ALL',
      },
      {
        label: 'Appointments only',
        value: 'APPOINTMENTS',
      },
      {
        label: 'Retail checkouts only',
        value: 'ORDERS',
      },
    ],
    [],
  );

  const inactiveEmployeesOptions: FilterOption[] = React.useMemo(
    () => [
      {
        label: 'Include inactive employees',
        value: 'true',
      },
      {
        label: 'Exclude inactive employees',
        value: 'false',
      },
    ],
    [],
  );

  const { resetScrollKeys, bump } = useReportsTables();

  const [reportRange, setReportRange] = React.useState<DateRange<Date>>(() =>
    getInitialFilterValue('ReportsDateRange', [], 'DATE_RANGE'),
  );

  // Wrap raw setters so a filter change automatically bumps the affected
  // tables' scroll-reset keys. Affinity rules:
  //   - selectedProducts: orders only (it scopes order line-item totals)
  //   - every other filter: all three tables
  // Not memoized via useCallback — generic type parameters can't be carried
  // through React's useCallback signature, so the result type loses <T>.
  const wrapFilterSetter =
    <T>(
      setter: React.Dispatch<React.SetStateAction<T>>,
      ...tables: ReportsTableName[]
    ) =>
    (next: React.SetStateAction<T>) => {
      setter(next);
      bump(...tables);
    };
  const [service, setService] = React.useState<ServiceCategory>(() =>
    getInitialFilterValue('ReportsServices', availableServices, 'SINGLE'),
  );
  const [dateType, setDateType] = React.useState<DateType>(() =>
    getInitialFilterValue(
      'ReportsDateType',
      dateTypeOptions,
      'SINGLE',
      'payment',
    ),
  );

  const [transactionType, setTransactionType] = React.useState<TransactionType>(
    () =>
      getInitialFilterValue(
        'ReportsTransactionType',
        ordersOptions,
        'SINGLE',
        'ALL',
      ),
  );
  const [selectedEmployees, setSelectedEmployees] = React.useState<string[]>(
    () => getInitialFilterValue('artists', availableEmployees, 'MULTI'),
  );
  const [selectedTrip, setSelectedTrip] = React.useState<string[]>(() =>
    getInitialFilterValue('trips', tripsOptions, 'MULTI'),
  );
  const [includeInactiveEmployees, setIncludeInactiveEmployees] =
    React.useState<boolean>(() =>
      getInitialFilterValue(
        'includeInactiveEmployees',
        inactiveEmployeesOptions,
        'SINGLE',
        !!user.business.inactiveEmployeesToBusinesses?.length &&
          user.role === Roles.SHOP_MANAGER &&
          user.canViewReports,
        true,
      ),
    );

  // Empty selection is treated as "all artists" — the API resolves it to every
  // employee the requester has access to.
  const isAllArtistsSelected = React.useMemo(
    () =>
      selectedEmployees.length === 0 ||
      availableEmployees.every(e => selectedEmployees.includes(e.value)),
    [availableEmployees, selectedEmployees],
  );

  const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);

  const isManager = user.role === Roles.SHOP_MANAGER && user.canViewReports;
  const includeOrdersInTotals = transactionType !== 'APPOINTMENTS';

  // selectedProducts only narrows /totals; list endpoints and /filters ignore it.
  const baseParams = React.useMemo(
    () => ({
      startDate: reportRange[0]!,
      endDate: reportRange[1]!,
      businessId: user.business.id,
      dateType,
      service,
      employeeId: selectedEmployees,
      tripIds: selectedTrip,
      transactionType: (transactionType ?? 'ALL') as TransactionType,
      isAllArtistsSelected,
      includeInactiveEmployees,
    }),
    [
      reportRange,
      user.business.id,
      dateType,
      service,
      selectedEmployees,
      selectedTrip,
      transactionType,
      isAllArtistsSelected,
      includeInactiveEmployees,
    ],
  );

  const hasRange = !!reportRange[0] && !!reportRange[1];

  const filtersQuery = useQuery({
    queryKey: ['reports/filters', baseParams],
    queryFn: () => fetchReportFilters(baseParams),
    enabled: !!user && hasRange,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const maxDateRangeMonths = filtersQuery.data?.maxDateRangeMonths ?? null;

  const isRangeWithinCap = React.useMemo(() => {
    if (!maxDateRangeMonths || !reportRange[0] || !reportRange[1]) return true;
    return reportRange[1] <= addMonths(reportRange[0], maxDateRangeMonths);
  }, [reportRange, maxDateRangeMonths]);

  // Wait for filters to settle so we know the cap before firing heavy queries.
  const queryEnabled =
    !!user && hasRange && filtersQuery.isSuccess && isRangeWithinCap;

  const baseQueryOptions = {
    enabled: queryEnabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  } as const;

  const totalsQuery = useQuery({
    queryKey: ['reports/totals', { ...baseParams, selectedProducts }],
    queryFn: () => fetchReportTotals({ ...baseParams, selectedProducts }),
    ...baseQueryOptions,
  });

  type AppointmentsPageParam = {
    appointmentsCursor?: string | null;
    unlinkedDepositsCursor?: string | null;
    skipAppointments?: boolean;
    skipUnlinkedDeposits?: boolean;
  };
  const appointmentsQuery = useInfiniteQuery({
    queryKey: ['reports/appointments', baseParams],
    queryFn: ({ pageParam }) =>
      fetchReportAppointmentsPage(baseParams, pageParam),
    getNextPageParam: page => {
      const { hasMore, cursor } = page.meta;
      if (!hasMore.appointments && !hasMore.unlinkedDeposits) return undefined;
      return {
        appointmentsCursor: hasMore.appointments
          ? cursor.appointments
          : undefined,
        unlinkedDepositsCursor: hasMore.unlinkedDeposits
          ? cursor.unlinkedDeposits
          : undefined,
        skipAppointments: !hasMore.appointments,
        skipUnlinkedDeposits: !hasMore.unlinkedDeposits,
      };
    },
    initialPageParam: {} as AppointmentsPageParam,
    ...baseQueryOptions,
  });

  const additionalServicesQuery = useInfiniteQuery({
    queryKey: ['reports/additional-services', baseParams],
    queryFn: ({ pageParam }) =>
      fetchReportAdditionalServicesPage(baseParams, {
        cursor: pageParam ?? null,
      }),
    getNextPageParam: page =>
      page.meta.hasMore ? page.meta.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: queryEnabled && !isManager,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const ordersParams = React.useMemo(
    () => ({ ...baseParams, selectedProducts }),
    [baseParams, selectedProducts],
  );

  const ordersQuery = useInfiniteQuery({
    queryKey: ['reports/orders', ordersParams],
    queryFn: ({ pageParam }) =>
      fetchReportOrdersPage(ordersParams, { cursor: pageParam ?? null }),
    getNextPageParam: page =>
      page.meta.hasMore ? page.meta.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: queryEnabled && includeOrdersInTotals && isManager,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const productOptions: FilterOption[] = React.useMemo(
    () =>
      (filtersQuery.data?.productOptions ?? []).map(name => ({
        label: name || '',
        value: name || '',
      })),
    [filtersQuery.data],
  );

  React.useEffect(() => {
    if (productOptions.length > 0) {
      const initialProducts = getInitialFilterValue(
        'products',
        productOptions,
        'MULTI',
      );
      setSelectedProducts(initialProducts);
    }
  }, [productOptions]);

  return {
    totalsQuery,
    filtersQuery,
    appointmentsQuery,
    additionalServicesQuery,
    ordersQuery,
    // Snapshot the current filter set — consumers pass it through to the
    // export endpoint so the worker can reproduce the exact query.
    exportFilters: { ...baseParams, selectedProducts },
    state: {
      resetScrollKeys,
      dateTypeFilter: {
        name: 'ReportsDateType',
        options: dateTypeOptions,
        value: dateType,
        setValue: wrapFilterSetter(setDateType, ...REPORTS_TABLE_NAMES),
      },
      servicesFilter: {
        name: 'ReportsServices',
        options: availableServices,
        value: service,
        setValue: wrapFilterSetter(setService, ...REPORTS_TABLE_NAMES),
      },
      artistFilter: {
        name: 'artists',
        options: availableEmployees,
        value: selectedEmployees,
        setValue: wrapFilterSetter(
          setSelectedEmployees,
          ...REPORTS_TABLE_NAMES,
        ),
        isAllSelected: isAllArtistsSelected,
      },
      tripFilter: {
        name: 'trips',
        options: tripsOptions,
        value: selectedTrip,
        setValue: wrapFilterSetter(setSelectedTrip, ...REPORTS_TABLE_NAMES),
      },
      transactionTypeFilter: {
        name: 'ReportsTransactionType',
        options: ordersOptions,
        value: transactionType,
        setValue: wrapFilterSetter(setTransactionType, ...REPORTS_TABLE_NAMES),
      },
      productsFilter: {
        name: 'products',
        options: productOptions,
        value: selectedProducts,
        setValue: wrapFilterSetter(setSelectedProducts, 'orders'),
      },
      inactiveEmployeesFilter: {
        name: 'includeInactiveEmployees',
        options: inactiveEmployeesOptions,
        value: includeInactiveEmployees,
        setValue: wrapFilterSetter(
          setIncludeInactiveEmployees,
          ...REPORTS_TABLE_NAMES,
        ),
      },
      reportRange,
      setReportRange: wrapFilterSetter(setReportRange, ...REPORTS_TABLE_NAMES),
      showArtistFilter: user.role === Roles.SHOP_MANAGER && user.canViewReports,
      showTripsFilter: user.business.businessTrips.length > 0,
      showOrdersOptions:
        user.role === Roles.SHOP_MANAGER && user.canViewReports,
      showProductsOptions: transactionType !== 'APPOINTMENTS',
      showInactiveEmployeesOption:
        !!user.business.inactiveEmployeesToBusinesses?.length &&
        user.role === Roles.SHOP_MANAGER &&
        user.canViewReports,
      isManager,
      userId: user.userId,
      maxDateRangeMonths,
      isRangeWithinCap,
    },
  };
}

export default useReports;
